import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardBody, Container, Row, Col, Table, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import SimpleHeader from '../../components/Headers/SimpleHeader';
import {
  useStatisticMonth,
  useStatisticSurvey,
  useStatisticVisit,
  useStatisticWeekday
} from '../../utils/api/react-query-api-client';

const colors = [
  '#058DC7',
  '#50B432',
  '#ED561B',
  '#DDDF00',
  '#24CBE5',
  '#64E572',
  '#FF9655',
  '#FFF263',
  '#6AF9C4'
];
const lighterColor = ['#50afd7', '#84ca6f', '#f2885f', '#e7e84c'];

const defaultPieData = {
  size: 80,
  showInLegend: false,
  dataLabels: {
    distance: -1,
    format: '{point.name}<br/>{point.percentage:.1f}%',
    color: '#000',
    style: {
      textOutline: 'none'
    }
  }
};

Highcharts.theme = {
  colors,
  chart: {
    margin: [20, 20, 70, 30],
    style: {
      fontFamily: 'Open Sans'
    }
  },
  title: {
    align: 'left',
    style: { color: '#000000', fontWeight: 'bold' },
    x: 80,
    y: 30
  },
  subtitle: {
    align: 'left',
    style: { color: '#000', lineHeight: 20, width: 700 },
    x: 80,
    y: 50
  },
  xAxis: {
    gridLineWidth: 1,
    lineColor: '#000',
    tickColor: '#000'
  },
  yAxis: {
    minorTickInterval: 'auto',
    lineColor: '#000',
    lineWidth: 1,
    tickWidth: 1,
    tickColor: '#000'
  }
};

const highchartsOptions = Highcharts.setOptions(Highcharts.theme);

const screenWidth =
  window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

// console.log('screenWidth', screenWidth);
let pieStart = 0;
let pieMargin = 40;
let labelMarginB = 150;

if (screenWidth > 1100) {
  pieStart = 35;
  pieMargin = 20;
  labelMarginB = 100;
} else if (screenWidth > 700) {
  pieStart = 20;
  pieMargin = 25;
} else if (screenWidth > 500) {
  pieStart = 0;
  pieMargin = 30;
}

const Statistic = () => {
  const { t } = useTranslation();

  const [monthStats, setMonthStats] = useState(null);
  const [weekdayStats, setWeekdayStats] = useState(null);
  const [surveyStats, setSurveyStats] = useState(null);

  const { data: visitInformation, isLoading: visitInformationIsLoading } = useStatisticVisit();
  const { data: statsMonth, isLoading: statsMonthIsLoading } = useStatisticMonth();
  const { data: statsWeekday, isLoading: statsWeekdayIsLoading } = useStatisticWeekday();
  const { data: statsSurvey, isLoading: statsSurveyisLoading } = useStatisticSurvey();

  useEffect(() => {
    document.body.classList.add('statistic');
    return () => document.body.classList.remove('statistic');
  }, []);

  useEffect(() => {
    if (!statsMonth || statsMonth.length === 0) return;

    const years = Object.keys(statsMonth);
    const labels = statsMonth[years[0]].map((m) => m.month);
    const series = years.map((y) => ({ name: y, data: statsMonth[y].map((m) => m.cnt) }));

    const options = {
      title: {
        text: ''
      },
      series,
      xAxis: {
        categories: labels
      },
      yAxis: {
        title: {
          text: t('default.qty')
        }
      }
    };

    setMonthStats(options);
  }, [statsMonth]);

  useEffect(() => {
    if (!statsWeekday || statsWeekday.length === 0) return;

    const years = Object.keys(statsWeekday);

    const labels = statsWeekday[years[0]].map((m) => t(`default.weekdays.${[m.day]}`));
    let series = years.map((y) => ({ name: y, data: statsWeekday[y].map((m) => m.cnt) }));
    series = [
      ...series,
      ...years.map((y, idx) => ({
        name: y,
        type: 'pie',
        data: [
          {
            name: t('statistic-overview.weekend', 'Helg'),
            y: statsWeekday[y].filter((m) => m.day >= 5).reduce((p, m) => (p += m.cnt), 0),
            color: lighterColor[idx]
          },
          {
            name: t('statistic-overview.weekdays', 'Vardagar'),
            y: statsWeekday[y].filter((m) => m.day < 5).reduce((p, m) => (p += m.cnt), 0),
            color: highchartsOptions.colors[idx]
          }
        ],
        ...defaultPieData,
        center: [`${pieStart + idx * pieMargin}%`, -60]
      }))
    ];

    const options = {
      chart: {
        margin: [90, 20, labelMarginB / 1.5, 80]
      },
      title: {
        text: ''
      },
      series,
      xAxis: {
        categories: labels
      },
      yAxis: {
        title: {
          text: t('default.qty')
        }
      }
    };

    setWeekdayStats(options);
  }, [statsWeekday]);

  useEffect(() => {
    if (!statsSurvey || statsSurvey.length === 0) return;

    const years = Object.keys(statsSurvey);

    const labels = Object.keys(statsSurvey[years[0]])
      .filter((k) => k.substr(0, 1) === 'Q')
      .map((k) => t(`statistic-overview.questions.${k}`));
    // questions[k]);

    let series = years.map((y) => ({
      name: y,
      type: y === 'average' ? 'spline' : 'column',
      color: y === 'average' ? 'black' : null,
      data: Object.keys(statsSurvey[y])
        .filter((k) => k.substr(0, 1) === 'Q')
        .map((k) => statsSurvey[y][k])
    }));

    series = [
      ...series,
      ...years.map((y, idx) => ({
        name: y,
        type: 'pie',
        data: [
          {
            name: '',
            y: statsSurvey[y].proc,
            color: highchartsOptions.colors[idx]
          },
          {
            name: '',
            y: 100 - statsSurvey[y].proc,
            color: lighterColor[idx]
          }
        ],
        ...defaultPieData,
        center: [`${pieStart + idx * pieMargin}%`, -50]
      }))
    ];

    const options = {
      chart: {
        margin: [90, 20, labelMarginB, 80]
      },
      title: {
        text: ''
      },
      series,
      xAxis: {
        categories: labels
      },
      yAxis: {
        title: {
          text: t('statistic-overview.grade', 'Betyg')
        },
        max: 5
      }
    };

    setSurveyStats(options);
  }, [statsSurvey]);

  return (
    <>
      <SimpleHeader name={t('statistic-overview.title', 'Statistik')} />

      <Container className="mt--6" fluid>
        <Row>
          <Col xs="12" md="9">
            <Row>
              <Col>
                <Card>
                  <CardHeader>
                    <h3 className="mb-0">
                      {t('statistic-overview.sub-title', 'Information om besökarna')}
                    </h3>
                  </CardHeader>
                  <CardBody>
                    {visitInformationIsLoading && <Spinner />}
                    <Table responsive>
                      <thead className="thead-light">
                        <tr>
                          <th>{t('statistic-overview.year', 'År')}</th>
                          <th>{t('statistic-overview.visitors', 'Besökare')}</th>
                          <th>PLUS</th>
                          <th>{t('statistic-overview.men', 'Män')}</th>
                          <th>{t('statistic-overview.women', 'Kvinnor')}</th>
                          <th>{t('statistic-overview.na', 'N/A')}</th>
                          <th>{t('statistic-overview.average-age', 'Snittålder')}</th>
                          <th>{t('statistic-overview.disatance', 'Körsträcka')}</th>
                          <th>{t('statistic-overview.travel-time', 'Restid')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitInformation?.map((v) => (
                          <tr key={v.year}>
                            <td>{v.year}</td>
                            <td>{v.total}</td>
                            <td>{v.ghPlus}</td>
                            <td>{v.men}</td>
                            <td>{v.women}</td>
                            <td>{v.na}</td>
                            <td>{v.age}</td>
                            <td>{v.distanceKm}</td>
                            <td>{v.time}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <span className="small mt-2 d-block">
                      {t(
                        'statistic-overview.distance-average',
                        'Körsträcka och restid är genomsnittet för samtliga besökare'
                      )}
                    </span>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col>
                <Card>
                  <CardHeader>
                    <h3 className="mb-0">
                      {t('statistic-overview.visitors-month', 'Besökare per månad')}
                    </h3>
                  </CardHeader>
                  <CardBody>
                    {statsMonthIsLoading && <Spinner />}
                    <HighchartsReact highcharts={Highcharts} options={monthStats} />
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col>
                <Card>
                  <CardHeader>
                    <h3 className="mb-0">
                      {t('statistic-overview.visitors-weekdays', 'Antal besök per veckodag')}
                    </h3>
                  </CardHeader>
                  <CardBody>
                    {statsWeekdayIsLoading && <Spinner />}
                    <HighchartsReact highcharts={Highcharts} options={weekdayStats} />
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col>
                <Card>
                  <CardHeader>
                    <h3 className="mb-0">{t('statistic-overview.survey', 'Enkätundersökning')}</h3>
                  </CardHeader>
                  <CardBody>
                    {statsSurveyisLoading && <Spinner />}
                    <HighchartsReact highcharts={Highcharts} options={surveyStats} />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
          <Col xs="12" md="3">
            <Card>
              <CardHeader>
                <h3 className="mb-0">
                  {t('statistic-overview.other-statistic', 'Övrig statistik')}
                </h3>
              </CardHeader>
              <CardBody>
                <div className="d-flex flex-row align-items-center">
                  <i className="ni ni-bold-right" />
                  <Link className="small" to="/golfclub-admin/statistic/visitors">
                    {t('statistic-overview.visitors-list', 'Lista besökare')}
                  </Link>
                </div>
                <div className="d-flex flex-row align-items-center mt-2">
                  <i className="ni ni-bold-right" />
                  <Link className="small" to="/golfclub-admin/statistic/map">
                    {t('statistic-overview.visitors-map', 'Visa besökare på karta')}
                  </Link>
                </div>
                <div className="d-flex flex-row align-items-center mt-2">
                  <i className="ni ni-bold-right" />
                  <Link className="small" to="/golfclub-admin/statistic/comments">
                    {t('statistic-overview.show-comments', 'Visa kommentarer')}
                  </Link>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Statistic;
